import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Box, CircularProgress, IconButton, styled, TextField, Typography } from "@mui/material";
import React, { useMemo, useState } from "react";
import { useFieldArray, useWatch } from "react-hook-form";

const ErrorText = styled("div")`
  color: red;
  font-size: 12px;
  margin-top: 4px;
`;

const CardWrap = styled(Box)(() => ({
  padding: "26px 150px",
  background: "#ffffff",
  maxWidth: "1030px",
  marginLeft: "auto",
  marginRight: "auto",
  marginBottom: "16px",
  boxShadow: "0 0 5px 0 rgba(0, 0, 0, 0.05)",
  borderRadius: "12px",
  "@media (max-width: 1199px)": {
    maxWidth: "100%",
    paddingLeft: "100px",
    paddingRight: "100px",
  },
  "@media (max-width: 991px)": {
    paddingLeft: "50px",
    paddingRight: "50px",
  },
  "@media (max-width: 575px)": {
    paddingLeft: "15px",
    paddingRight: "15px",
  },
}));

const AreaTitle = styled(Typography)(() => ({
  fontSize: "20px",
  fontWeight: "600",
  lineHeight: "1.35",
  color: "#000000",
  marginBottom: "14px",
}));

const CustomTextField = styled(TextField)(({ error = false }) => ({
  width: "100%",
  border: error ? "1px solid rgb(210, 33, 33)" : "1px solid #DADADA",
  borderRadius: "10px",
  background: "#FFFFFF",
  "& .MuiInputBase-input": {
    fontSize: "15px",
    fontWeight: "300",
    lineHeight: "1",
    color: "#2C2C2E",
    padding: "12px 16px",
  },
  "& fieldset": {
    border: "none",
  },
}));

const RowWrap = styled(Box)(() => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "flex-start",
  gap: "10px",
  marginBottom: "10px",
  position: "relative",
}));

const ActivityRow = styled(Box)(() => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "10px",
  padding: "10px",
  borderRadius: "12px",
  background: "#FFFFFF",
  border: "1px solid #ECECEC",
  transition: "border-color 140ms ease, box-shadow 140ms ease, background 140ms ease",
  "&:hover": {
    borderColor: "#D9D9D9",
    background: "rgba(16, 133, 171, 0.02)",
  },
}));

const FooterRow = styled(Box)(() => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  columnGap: "10px",
  marginTop: "10px",
}));

const InlineStat = styled(Box)(() => ({
  fontSize: "14px",
  fontWeight: "600",
  color: "#2C2C2E",
  textAlign: "center",
  marginBottom: "0",
}));

function reorderArray(list, fromIndex, toIndex) {
  const result = [...list];
  const [removed] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, removed);
  return result;
}

const CoreFocusAreaActivitiesCard = ({
  control,
  register,
  watch,
  getValues,
  errors,
  focusArea,
  focusAreaIndex,
  employeeId,
  createCriticalActivity,
}) => {
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isAutoSavingReorder, setIsAutoSavingReorder] = useState(false);
  const [reorderSaveError, setReorderSaveError] = useState(null);

  const subDataName = `coreFocusAreas.${focusAreaIndex}.subData`;
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: subDataName,
    keyName: "fieldId",
  });

  const rows = useWatch({
    control,
    name: subDataName,
    defaultValue: [],
  });

  const totalTime = useMemo(() => {
    return (rows || []).reduce((acc, subTask) => acc + Number(subTask?.timeSpent || 0), 0);
  }, [rows]);

  const hasTimeSpentValue = useMemo(() => {
    return (rows || []).some((subTask) => String(subTask?.timeSpent ?? "").trim().length > 0);
  }, [rows]);

  const handleDragStart = (index, event) => {
    if (isAutoSavingReorder) return;
    setDraggingIndex(index);
    if (!event?.dataTransfer) return;
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(index));
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (event?.dataTransfer) event.dataTransfer.dropEffect = "move";
  };

  const handleDragEnterRow = (index) => {
    if (isAutoSavingReorder) return;
    if (dragOverIndex !== index) setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    setDraggingIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = async (toIndex, event) => {
    if (isAutoSavingReorder) return;
    event.preventDefault();
    const dataTransfer = event?.dataTransfer;
    if (!dataTransfer) return;
    const fromIndex = Number(dataTransfer.getData("text/plain"));
    if (!Number.isInteger(fromIndex) || fromIndex === toIndex) {
      handleDragEnd();
      return;
    }

    setIsAutoSavingReorder(true);
    setReorderSaveError(null);
    try {
      const allAreas = getValues("coreFocusAreas") || [];
      const currentArea = allAreas?.[focusAreaIndex] || {};
      const currentSubData = currentArea?.subData || [];
      const reorderedSubData = reorderArray(currentSubData, fromIndex, toIndex);

      // Update RHF first for immediate UI response.
      move(fromIndex, toIndex);
      handleDragEnd();

      if (!employeeId) return;

      // Persist the full payload (all areas) to avoid backend side-effects.
      const nextAreas = allAreas.map((area, idx) =>
        idx === focusAreaIndex ? { ...area, subData: reorderedSubData } : area
      );
      const payload = nextAreas.flatMap((area) =>
        (area?.subData || []).map((subTask) => ({
          user_id: employeeId,
          core_focus_area_id: area.id,
          area: subTask.subTask,
          importance: subTask.timeSpent,
        }))
      );

      await createCriticalActivity(payload).unwrap();
    } catch (error) {
      const message =
        error?.data?.message || error?.message || "Failed to save order. Please try again.";
      setReorderSaveError(message);
      // Keep console for debugging in non-prod builds; avoid throwing to UI.
      // eslint-disable-next-line no-console
      console.error("Auto-save reorder failed:", error);
    } finally {
      setIsAutoSavingReorder(false);
    }
  };

  return (
    <CardWrap>
      <AreaTitle>{focusArea?.area || ""}</AreaTitle>

      {fields.map((subField, subIndex) => {
        const subTaskError =
          errors?.coreFocusAreas?.[focusAreaIndex]?.subData?.[subIndex]?.subTask;
        const timeSpentError =
          errors?.coreFocusAreas?.[focusAreaIndex]?.subData?.[subIndex]?.timeSpent;

        return (
          <ActivityRow
            key={subField.fieldId}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(subIndex, e)}
            onDragEnd={handleDragEnd}
            onDragEnter={() => handleDragEnterRow(subIndex)}
            sx={{
              opacity: draggingIndex === subIndex ? 0.65 : 1,
              boxShadow:
                dragOverIndex === subIndex && draggingIndex !== null
                  ? "0 0 0 3px rgba(16, 133, 171, 0.12)"
                  : "none",
              borderColor:
                dragOverIndex === subIndex && draggingIndex !== null ? "#1085AB" : "#ECECEC",
            }}
          >
            <Box
              sx={{
                width: "40px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                paddingTop: { xs: "6px", sm: "0" },
              }}
            >
              <IconButton
                size="small"
                disabled={isAutoSavingReorder}
                draggable={!isAutoSavingReorder}
                onDragStart={(e) => handleDragStart(subIndex, e)}
                onDragEnd={handleDragEnd}
                sx={{
                  cursor: isAutoSavingReorder ? "not-allowed" : "grab",
                  color: "#9AA3A7",
                  "&:hover": { background: "rgba(16, 133, 171, 0.08)", color: "#1085AB" },
                }}
                aria-label="Drag to reorder activity"
              >
                <DragIndicatorIcon fontSize="small" />
              </IconButton>
            </Box>

            <RowWrap sx={{ flex: 1, marginBottom: "0" }}>
              <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: "260px" } }}>
                <CustomTextField
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "10px 16px",
                    },
                  }}
                  variant="outlined"
                  placeholder="Activity"
                  {...register(`coreFocusAreas.${focusAreaIndex}.subData.${subIndex}.subTask`)}
                  error={!!subTaskError}
                />
                {subTaskError && <ErrorText>{subTaskError?.message}</ErrorText>}
              </Box>

              <Box sx={{ width: { xs: "calc(50% - 25px)", sm: "120px" } }}>
                <CustomTextField
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "10px 16px",
                      textAlign: "right",
                    },
                  }}
                  variant="outlined"
                  placeholder="%"
                  {...register(`coreFocusAreas.${focusAreaIndex}.subData.${subIndex}.timeSpent`)}
                  error={!!timeSpentError}
                />
                {timeSpentError && <ErrorText>{timeSpentError?.message}</ErrorText>}
              </Box>

              <Box
                sx={{
                  width: { xs: "40px", sm: "44px" },
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginLeft: { xs: "auto", sm: "0" },
                  paddingTop: { xs: "6px", sm: "0" },
                }}
              >
                <IconButton
                  size="small"
                  type="button"
                  onClick={() => remove(subIndex)}
                  disabled={isAutoSavingReorder}
                  sx={{
                    width: "32px",
                    height: "32px",
                    background: "#F6F6F6",
                    border: "1px solid #EFEFEF",
                    color: "#E75B5B",
                    "&:hover": { background: "#FDECEC", borderColor: "#F4C9C9" },
                  }}
                  aria-label="Remove activity"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 1L15 15M15 1L1 15"
                      stroke="#E75B5B"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </IconButton>
              </Box>
            </RowWrap>
          </ActivityRow>
        );
      })}

      {isAutoSavingReorder && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "6px" }}>
          <CircularProgress size={16} />
          <Typography sx={{ fontSize: "14px", color: "#7C7C7C" }}>Saving order…</Typography>
        </Box>
      )}
      {reorderSaveError && <ErrorText style={{ marginTop: "8px" }}>{reorderSaveError}</ErrorText>}

      <FooterRow>
        <Box sx={{ flex: 1 }}>
          <IconButton
            type="button"
            onClick={() => append({ subTask: "", timeSpent: "" })}
            disabled={isAutoSavingReorder}
            sx={{
              height: "38px",
              padding: "0 12px",
              borderRadius: "10px",
              background: "#F0F0F0",
              border: "1px solid #F0F0F0",
              color: "#1085AB",
              fontWeight: 700,
              fontSize: "14px",
              "&:hover": { background: "#EAEAEA" },
            }}
            aria-label="Add activity"
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.10767 1L8.10767 15"
                  stroke="#F28021"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M1 8.18335L15 8.18335"
                  stroke="#F28021"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              Add
            </Box>
          </IconButton>
        </Box>

        <Box sx={{ width: "120px" }}>
          <InlineStat>
            {totalTime}%
            {totalTime > 100 && <ErrorText>Total percentage cannot exceed 100%!</ErrorText>}
            {hasTimeSpentValue && totalTime < 100 && <ErrorText>Total percentage cannot under 100%!</ErrorText>}
          </InlineStat>
        </Box>
      </FooterRow>
    </CardWrap>
  );
};

export default CoreFocusAreaActivitiesCard;

